import { useState, useCallback } from 'react';
import { User, Entrepreneur, Investor } from '../types';
import { ConnectionService } from '../services/connectionService';

/**
 * Hook personalizado para manejar la lógica de swipe en tarjetas de usuario
 * 
 * Funcionalidades principales:
 * - Navegación entre usuarios (currentUser, nextUser)
 * - Acciones de swipe (left/right/super like)
 * - Gestión de conexiones exitosas
 * - Función de rewind para deshacer última acción
 * 
 * @param users - Array de usuarios para mostrar
 * @returns Objeto con estado actual y funciones de control
 */
export const useSwipe = (users: User[]) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [connections, setConnections] = useState<User[]>([]);
  const [rejectedUsers, setRejectedUsers] = useState<User[]>([]);

  const currentUser = users[currentIndex];
  const nextUser = users[currentIndex + 1];

  // Acción de rechazo - mueve al siguiente usuario
  const swipeLeft = useCallback(() => {
    if (currentUser) {
      setRejectedUsers(prev => [...prev, currentUser]);
      setCurrentIndex(prev => prev + 1);
    }
  }, [currentUser]);

  // Acción de interés - verifica si hay conexión mutua
  const swipeRight = useCallback(() => {
    if (currentUser) {
      const isConnection = ConnectionService.checkConnection();
      
      if (isConnection) {
        setConnections(prev => [...prev, currentUser]);
      }
      
      setCurrentIndex(prev => prev + 1);
      return isConnection;
    }
    return false;
  }, [currentUser]);

  // Super like - mayor probabilidad de conexión
  const superLike = useCallback(() => {
    if (currentUser) {
      const isConnection = ConnectionService.checkConnection(true);
      
      if (isConnection) {
        setConnections(prev => [...prev, currentUser]);
      }
      
      setCurrentIndex(prev => prev + 1);
      return isConnection;
    }
    return false;
  }, [currentUser]);

  // Función para deshacer la última acción
  const rewind = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
      // Remove from rejected/connections if it was the last action
      setRejectedUsers(prev => prev.slice(0, -1));
      setConnections(prev => prev.slice(0, -1));
    }
  }, [currentIndex]);

  // Reinicia el estado del swipe
  const reset = useCallback(() => {
    setCurrentIndex(0);
    setConnections([]);
    setRejectedUsers([]);
  }, []);

  return {
    currentUser,
    nextUser,
    connections,
    rejectedUsers,
    swipeLeft,
    swipeRight,
    superLike,
    rewind,
    reset,
    hasMore: currentIndex < users.length,
    canRewind: currentIndex > 0
  };
};